// ---------------------------------------------------------------------------
// Line chart — pure data → SVG string
// ---------------------------------------------------------------------------

import { svgText, svgAttr, scaleLinear, numberFormat, round2 } from './svg-util.js';
import { chartTitleDesc } from './chart-a11y.js';

/**
 * @typedef {Object} LineChartOpts
 * @property {string} idPrefix
 * @property {string} [title]
 * @property {string} [desc]
 * @property {number} [width]
 * @property {number} [height]
 */

/**
 * Render a single-series line chart over a time/ordinal series. Points are
 * evenly spaced on x; y runs 0..max. Draws a polyline plus point markers and a
 * trailing value label.
 *
 * @param {{ date: string, value: number }[]} series
 * @param {LineChartOpts} opts
 * @returns {string}
 */
export function lineChart(series, opts) {
  const { idPrefix, title = 'Line chart', desc = '', width = 320, height = 160 } = opts;
  const padX = 10;
  const padTop = 14;
  const padBottom = 22;
  const plotW = Math.max(0, width - padX * 2);
  const plotH = Math.max(0, height - padTop - padBottom);
  const safe = Array.isArray(series) ? series : [];
  const max = safe.reduce((m, p) => Math.max(m, Number(p?.value) || 0), 0);

  const { titleId, descId, svgDefs } = chartTitleDesc(
    title,
    desc || `Line chart with ${safe.length} points.`,
    idPrefix,
  );

  const n = safe.length;
  const step = n > 1 ? plotW / (n - 1) : 0;

  const points = safe.map((p, i) => {
    const value = Number(p?.value) || 0;
    const x = round2(padX + i * step);
    const y = round2(padTop + (plotH - scaleLinear(value, 0, max, 0, plotH)));
    return { x, y, value };
  });

  const polyline = points.length
    ? `<polyline class="chart-line" fill="none" points="${points.map((p) => `${p.x},${p.y}`).join(' ')}"></polyline>`
    : '';

  const markers = points
    .map((p) => `<circle class="chart-line-point" cx="${p.x}" cy="${p.y}" r="2.5"></circle>`)
    .join('');

  const last = points[points.length - 1];
  const lastLabel = last
    ? `<text class="chart-line-value" x="${round2(last.x)}" y="${round2(last.y - 6)}" text-anchor="end">${svgText(numberFormat(last.value))}</text>`
    : '';

  return (
    `<svg class="chart chart--line" viewBox="0 0 ${svgAttr(width)} ${svgAttr(height)}" ` +
    `preserveAspectRatio="xMidYMid meet" aria-labelledby="${svgAttr(titleId)} ${svgAttr(descId)}">` +
    svgDefs +
    polyline +
    markers +
    lastLabel +
    `</svg>`
  );
}
